import React from "react";
import {
  Sheet, SheetContent, SheetDescription,
  SheetHeader, SheetTitle, SheetTrigger, SheetFooter,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { Settings, Sun, Moon, Monitor, RotateCcw, Check, Palette, Sparkles } from "lucide-react";
import useSettingsStore from "../../store/useSettingsStore";
import { cn } from "../../lib/utils";

const PRESET_COLORS = [
  { label: 'primary', value: '#7c5e41' },
  { label: 'Violet', value: '#8b5cf6' },
  { label: 'Blue', value: '#3b82f6' },
  { label: 'Navy', value: '#133458' },
  { label: 'Teal', value: '#14b8a6' },
  { label: 'Plum', value: '#381332' },
  { label: 'Rose', value: '#f43f5e' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Amber', value: '#f59e0b' },
  { label: 'Pink', value: '#ec4899' },
];

const ThemeCard = ({ icon: Icon, label, active, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      "flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all duration-200 text-xs font-medium w-full",
      active
        ? "border-primary bg-primary/5 text-primary"
        : "border-border hover:border-border/80 hover:bg-muted/50 text-muted-foreground"
    )}
  >
    <Icon className="h-5 w-5" />
    <span>{label}</span>
    {active && <Badge className="text-[9px] h-3.5 px-1 bg-primary/10 text-primary border-primary/20">Active</Badge>}
  </button>
);

const SettingRow = ({ label, desc, children }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="space-y-0.5 min-w-0">
      <Label className="text-sm font-medium">{label}</Label>
      {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
    </div>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground/60 mb-3">
    {children}
  </h3>
);

const SettingsDrawer = () => {
  const {
    layoutType, setLayoutType,
    layoutContainer, setLayoutContainer,
    sidebarMode, setSidebarMode,
    headerType, setHeaderType,
    footerType, setFooterType,
    theme, setTheme,
    rtl, toggleRtl,
    cardStyle, setCardStyle,
    primaryColor, setPrimaryColor,
    resetSettings,
  } = useSettingsStore();

  return (
    <Sheet>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="fixed bottom-6 right-6 h-12 w-12 rounded-full shadow-lg bg-background border-border z-50 hover:bg-muted/80 hover:text-primary transition-all duration-300"
              >
                <Settings className="h-5 w-5 animate-spin-slow" />
              </Button>
            </SheetTrigger>
          </TooltipTrigger>
          <TooltipContent side="left">Customize theme</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <SheetContent className="w-80 sm:w-96 flex flex-col gap-0 p-0">
        {/* Header */}
        <SheetHeader className="px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Palette className="h-4 w-4 text-primary" />
            </div>
            <div>
              <SheetTitle className="text-base font-bold">Customization</SheetTitle>
              <SheetDescription className="text-xs mt-0">
                Personalize your dashboard experience
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 py-5 space-y-6">

            {/* ── Theme Mode ────────────────────────── */}
            <div>
              <SectionTitle>Color Mode</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                <ThemeCard icon={Sun} label="Light" active={theme === 'light'} onClick={() => setTheme('light')} />
                <ThemeCard icon={Moon} label="Dark" active={theme === 'dark'} onClick={() => setTheme('dark')} />
                <ThemeCard icon={Monitor} label="System" active={theme === 'system'} onClick={() => setTheme('system')} />
                <ThemeCard icon={Sparkles} label="Galaxy" active={theme === 'galaxy'} onClick={() => setTheme('galaxy')} />
              </div>
            </div>

            <Separator />

            {/* ── Primary Color ─────────────────────── */}
            <div>
              <SectionTitle>Primary Color</SectionTitle>
              <div className="grid grid-cols-8 gap-2">
                {PRESET_COLORS.map(color => (
                  <TooltipProvider key={color.value} delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setPrimaryColor(color.value)}
                          className={cn(
                            "w-full aspect-square rounded-lg border-2 transition-all duration-150 flex items-center justify-center",
                            primaryColor === color.value ? "border-foreground scale-110" : "border-transparent hover:scale-105"
                          )}
                          style={{ backgroundColor: color.value }}
                        >
                          {primaryColor === color.value && (
                            <Check className="h-3.5 w-3.5 text-white drop-shadow" />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">{color.label}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>
              {/* Custom hex input */}
              <div className="mt-3 flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-lg border border-border shrink-0"
                  style={{ backgroundColor: primaryColor }}
                />
                <input
                  type="color"
                  value={primaryColor}
                  onChange={e => setPrimaryColor(e.target.value)}
                  className="flex-1 h-8 rounded-md border border-border bg-muted cursor-pointer"
                  title="Custom color"
                />
                <span className="text-xs text-muted-foreground font-mono">{primaryColor}</span>
              </div>
            </div>

            <Separator />

            {/* ── Layout ────────────────────────────── */}
            <div>
              <SectionTitle>Layout</SectionTitle>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Layout Type</Label>
                  <Select value={layoutType} onValueChange={setLayoutType}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vertical">Vertical</SelectItem>
                      <SelectItem value="horizontal">Horizontal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Layout Container</Label>
                  <Select value={layoutContainer} onValueChange={setLayoutContainer}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fluid">Fluid (100%)</SelectItem>
                      <SelectItem value="boxed">Boxed (Centered)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Sidebar Mode</Label>
                  <Select value={sidebarMode} onValueChange={setSidebarMode} disabled={layoutType === 'horizontal'}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full Sidebar</SelectItem>
                      <SelectItem value="collapsed">Collapsed</SelectItem>
                      <SelectItem value="icon-only">Icon Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Header Style</Label>
                  <Select value={headerType} onValueChange={setHeaderType}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sticky">Sticky (Fixed)</SelectItem>
                      <SelectItem value="static">Static</SelectItem>
                      <SelectItem value="hidden">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Footer Visibility</Label>
                  <Select value={footerType} onValueChange={setFooterType}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="static">Visible</SelectItem>
                      <SelectItem value="hidden">Hidden</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <SettingRow label="RTL Mode" desc="Right-to-left text direction">
                  <Switch checked={rtl} onCheckedChange={toggleRtl} />
                </SettingRow>

              </div>
            </div>

            <Separator />

            {/* ── Card Styles ───────────────────────── */}
            <div>
              <SectionTitle>Card Style</SectionTitle>
              <div className="space-y-4">
                <SettingRow label="Borders" desc="Show card border outlines">
                  <Switch
                    checked={cardStyle.border}
                    onCheckedChange={(c) => setCardStyle({ border: c })}
                  />
                </SettingRow>
                <SettingRow label="Shadows" desc="Show depth shadows on cards">
                  <Switch
                    checked={cardStyle.shadow}
                    onCheckedChange={(c) => setCardStyle({ shadow: c })}
                  />
                </SettingRow>

                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Border Radius</Label>
                  <div className="grid grid-cols-5 gap-1">
                    {[
                      { label: 'None', value: 'none' },
                      { label: 'SM', value: 'sm' },
                      { label: 'MD', value: 'md' },
                      { label: 'LG', value: 'lg' },
                      { label: 'XL', value: 'xl' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setCardStyle({ rounded: opt.value })}
                        className={cn(
                          "text-[9px] font-medium py-1.5 rounded border transition-all",
                          cardStyle.rounded === opt.value
                            ? "bg-primary text-primary-foreground border-primary font-bold shadow-sm"
                            : "border-border hover:bg-muted text-muted-foreground"
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <SheetFooter className="px-5 py-4 border-t border-border mt-auto">
          <Button
            variant="outline"
            className="w-full gap-2 hover:text-danger hover:border-danger/50"
            onClick={resetSettings}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset to Defaults
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default SettingsDrawer;
